import React from 'react'
import {
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
} from '../../../utils/chakraMonomorphic'

import copy from '../copy.json'

export interface LookupSubmission {
  personId: number
  clientId: number
}

interface Props {
  onSubmit: (submission: LookupSubmission) => void
  isLoading: boolean
}

/**
 * Person ID + Camp ID entry. Submit-driven on purpose: the diagnostics call is
 * expensive and cross-camp, so nothing is fetched on keystroke.
 *
 * The camp id is free text rather than the camp picker because the page is
 * explicitly cross-camp — the caller's `client.*` scope is what permits it, and
 * the server returns 403 for a camp they aren't scoped to.
 */
export const LookupForm: React.FC<Props> = ({
  onSubmit,
  isLoading,
}): React.ReactElement => {
  const [personId, setPersonId] = React.useState('')
  const [clientId, setClientId] = React.useState('')

  // Digits-only rather than parseInt. `parseInt('4242abc', 10)` returns 4242,
  // so a typo or a pasted-with-junk id would silently submit a DIFFERENT
  // camper than the operator typed — the worst failure mode for a tool whose
  // output gets quoted back to a parent.
  const isPositiveInteger = (raw: string): boolean =>
    /^\d+$/.test(raw) && Number(raw) > 0

  const personIdValid = isPositiveInteger(personId)
  const clientIdValid = isPositiveInteger(clientId)
  const isValid = personIdValid && clientIdValid

  // Only complain about a field the operator has actually typed in, so the form
  // isn't pre-littered with errors.
  const showPersonIdError = personId !== '' && !personIdValid
  const showClientIdError = clientId !== '' && !clientIdValid

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    if (!isValid) return
    onSubmit({personId: Number(personId), clientId: Number(clientId)})
  }

  return (
    <form onSubmit={handleSubmit}>
      <Flex gap={4} alignItems='flex-end' wrap='wrap' mb={6}>
        <FormControl maxW='200px'>
          <FormLabel fontSize='sm' htmlFor='fr-diagnostics-person-id'>
            {copy.lookup.personIdLabel}
          </FormLabel>
          <Input
            id='fr-diagnostics-person-id'
            // `inputMode` rather than type='number' so the field doesn't get
            // spinners or accept exponent notation.
            inputMode='numeric'
            value={personId}
            onChange={(e): void => setPersonId(e.target.value)}
          />
          {showPersonIdError && (
            <Text fontSize='xs' color='red.600' data-testid='person-id-error'>
              {copy.lookup.personIdRequired}
            </Text>
          )}
        </FormControl>

        <FormControl maxW='200px'>
          <FormLabel fontSize='sm' htmlFor='fr-diagnostics-camp-id'>
            {copy.lookup.campIdLabel}
          </FormLabel>
          <Input
            id='fr-diagnostics-camp-id'
            inputMode='numeric'
            value={clientId}
            onChange={(e): void => setClientId(e.target.value)}
          />
          {showClientIdError && (
            <Text fontSize='xs' color='red.600' data-testid='camp-id-error'>
              {copy.lookup.campIdRequired}
            </Text>
          )}
        </FormControl>

        <Button
          type='submit'
          isDisabled={!isValid}
          isLoading={isLoading}
          data-testid='fr-diagnostics-submit'>
          {copy.lookup.submit}
        </Button>
      </Flex>
    </form>
  )
}
